import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, SearchBar, Spinner, InfoRow, Input, Textarea,
} from './shared';

const INITIAL_FORM = {
    config_key: '', config_value: '', category: '', description: '',
};

export default function AdminSystemConfigs() {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    useEffect(() => { loadConfigs(); }, []);

    const loadConfigs = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listSystemConfigs(categoryFilter || undefined);
            setConfigs(res.configs || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load configs');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (config) => {
        setSelectedConfig(config);
        setIsViewMode(true);
        setIsCreateMode(false);
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleEdit = (config) => {
        setSelectedConfig({
            ...config,
            category: config.category || '',
            description: config.description || '',
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setIsCreateMode(false);
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedConfig(INITIAL_FORM);
        setIsCreateMode(true);
        setIsViewMode(false);
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleSave = async (data) => {
        try {
            const payload = {
                config_value: data.config_value,
                category: data.category || null,
                description: data.description || null,
            };

            if (isEditMode) {
                await adminApi.updateSystemConfig(data.config_key, payload);
                setSuccessMessage('Config updated');
            } else {
                await adminApi.createSystemConfig({ config_key: data.config_key, ...payload });
                setSuccessMessage('Config created');
            }
            setIsModalOpen(false);
            loadConfigs();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (configKey) => {
        if (!confirm(`Delete config "${configKey}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteSystemConfig(configKey);
            setSuccessMessage(`Config "${configKey}" deleted`);
            loadConfigs();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const categories = [...new Set(configs.map(c => c.category).filter(Boolean))].sort();

    const filteredConfigs = configs.filter(c => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return c.config_key.toLowerCase().includes(q) ||
            c.config_value.toLowerCase().includes(q) ||
            (c.category && c.category.toLowerCase().includes(q));
    });

    const grouped = {};
    filteredConfigs.forEach(c => {
        const cat = c.category || 'uncategorized';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(c);
    });
    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">System Configs</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {configs.length} config{configs.length !== 1 ? 's' : ''} — application-wide key-value configurations
                    </p>
                </div>
                <Button size="sm" onClick={handleCreate}>Add Config</Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onRefresh={loadConfigs}
                    placeholder="Search configs..."
                    className="flex-1"
                />
                {categories.length > 1 && (
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="h-8 px-3 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                )}
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading configs..." center />
            ) : sortedGroups.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No configs found</div>
            ) : (
                <div className="space-y-4">
                    {sortedGroups.map(([category, items]) => (
                        <div key={category}>
                            <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline">{category}</Badge>
                                <span className="text-xs text-gray-400">{items.length} config{items.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
                                {items.map((config) => (
                                    <div key={config.config_key} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                                        <div className="min-w-0 flex-1">
                                            <span className="text-sm font-medium text-gray-900 font-mono">{config.config_key}</span>
                                            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                                                {config.config_value.length > 80
                                                    ? config.config_value.slice(0, 80) + '...'
                                                    : config.config_value}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button variant="ghost" size="xs" onClick={() => handleView(config)}>View</Button>
                                            <Button variant="ghost" size="xs" onClick={() => handleEdit(config)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(config.config_key)}>Delete</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && isViewMode && selectedConfig && (
                <Modal open onClose={() => setIsModalOpen(false)} title={selectedConfig.config_key}>
                    <Modal.Body>
                        <InfoRow label="Key" value={selectedConfig.config_key} mono />
                        <InfoRow label="Category" value={selectedConfig.category || '-'} />
                        <InfoRow label="Description" value={selectedConfig.description || '-'} />
                        <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Value</p>
                            <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {selectedConfig.config_value}
                            </pre>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Close</Button>
                        <Button onClick={() => { setIsViewMode(false); handleEdit(selectedConfig); }}>Edit</Button>
                    </Modal.Footer>
                </Modal>
            )}

            {isModalOpen && (isCreateMode || isEditMode) && selectedConfig && (
                <ConfigEditModal
                    config={selectedConfig}
                    isEdit={isEditMode}
                    categories={categories}
                    onSave={handleSave}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
}

function ConfigEditModal({ config, isEdit, categories, onSave, onClose }) {
    const [formData, setFormData] = useState({ ...config });
    const [saving, setSaving] = useState(false);

    const handleChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try { await onSave(formData); } finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={isEdit ? `Edit: ${config.config_key}` : 'Add Config'}>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Input
                        label="Config Key"
                        value={formData.config_key}
                        onChange={(e) => handleChange('config_key', e.target.value)}
                        placeholder="category.setting_name"
                        required
                        disabled={isEdit}
                    />
                    <Textarea
                        label="Value"
                        monospace
                        rows={6}
                        value={formData.config_value}
                        onChange={(e) => handleChange('config_value', e.target.value)}
                        placeholder="Config value..."
                        required
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            label="Category"
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            placeholder="usage_limits"
                            list="categories-list"
                        />
                        <datalist id="categories-list">
                            {categories.map(c => <option key={c} value={c} />)}
                        </datalist>
                        <Input
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="What this config does..."
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                    <Button type="submit" loading={saving}>{isEdit ? 'Save Changes' : 'Create'}</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
